import React, { useState, useEffect } from "react";
import { OTPInput, REGEXP_ONLY_DIGITS } from "input-otp";
import { ArrowLeft, ArrowRight, BarChart3, Loader2, ShieldCheck, Smartphone, Users, KeyRound } from "lucide-react";
import api from "../utils/axios.ts";
import { config } from "../utils/config.ts";
import { Banner, SiteHeader, SiteFooter } from "../components/SiteChrome";
import { PhoneField, isUsablePhone } from "../components/PhoneField";
import { restoreLanguage, patchDomForTranslate } from "../utils/translate";

/* Sign-in page, in the same design language as the marketing site: soft brand gradient, pastel cards, pill buttons,
   Google Sans Flex. The API calls are unchanged: POST /generate-otp, then POST /verify-otp. */

const asset = (file) => process.env.PUBLIC_URL + "/" + file;
const font = { fontFamily: "'Google Sans Flex', 'Google Sans', Roboto, system-ui, sans-serif" };

const PERKS = [
  { Icon: BarChart3, title: "Track every contribution", text: "See each payment the moment it arrives.", tint: "#E8F0FE", ink: "#1967D2" },
  { Icon: Users, title: "Manage your members", text: "Add, edit and organise your whole group.", tint: "#E6F4EA", ink: "#137333" },
  { Icon: Smartphone, title: "Web, Android and WhatsApp", text: "One group, the same data everywhere.", tint: "#FEF7E0", ink: "#B06000" },
];

const Login = () => {
  const [step, setStep] = useState(1); // 1 = phone number, 2 = code
  const [phone, setPhone] = useState("");
  const phoneOrEmail = phone; // sign-in is by phone number only; the code goes to that number
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState("");

    useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  useEffect(() => { patchDomForTranslate(); restoreLanguage(); }, []);

  const requestCode = async () => {
    await api.post("/generate-otp", { username: phoneOrEmail });
    setResendTimer(60);
  };
  const messageOf = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

  const handleContinue = async (e) => {
    e.preventDefault();
    setError("");
    if (!isUsablePhone(phone)) return setError("Please enter a valid phone number for the selected country.");
    setIsLoading(true);
    try {
      await requestCode();
      setStep(2);
    } catch (err) {
      setError(messageOf(err, "Failed to send the code. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (code.length !== 6) return setError("Please enter the 6-digit code we sent you.");
    setIsVerifying(true);
    try {
      const { data } = await api.post("/verify-otp", { username: phoneOrEmail, otp: code });
      if (data?.token) localStorage.setItem("userToken", data.token);
      window.location.href = "/";
    } catch (err) {
      setError(messageOf(err, "That code isn't valid. Double-check it and try again."));
      setCode(""); // empty the boxes so the code can simply be typed again
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError("");
    setIsResending(true);
    try {
      await requestCode();
    } catch (err) {
      setError(messageOf(err, "Failed to resend the code. Please try again."));
    } finally {
      setIsResending(false);
    }
  };

  const back = () => {
    setStep(1);
    setCode("");
    setError("");
  };

  const pill = "inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#1A73E8] px-7 text-[15px] font-bold text-white transition-colors hover:bg-[#1765CC] disabled:cursor-not-allowed disabled:opacity-60";
  const link = "font-bold text-[#0B57D0] hover:underline";
  const site = config.backendUrl;

  return (
    <div className="login-light flex min-h-screen flex-col bg-[#ffffff]" style={font}>
      <style>{`html .login-light input.login-input,html.dark .login-light input.login-input,html .login-light input.phone-field__input,html.dark .login-light input.phone-field__input{background-color:#fff!important;color:#202124!important;border-color:#BDC1C6!important;outline:none!important}html .login-light input.login-input:focus{border-color:#1A73E8!important}html .login-light input[data-input-otp],html.dark .login-light input[data-input-otp]{background:transparent!important;background-color:transparent!important;color:transparent!important;border-color:transparent!important;box-shadow:none!important}.login-light .phone-field{--react-international-phone-height:56px;--react-international-phone-border-radius:14px;--react-international-phone-border-color:#BDC1C6;--react-international-phone-background-color:#fff;--react-international-phone-text-color:#202124;--react-international-phone-font-size:1rem;--react-international-phone-selected-dropdown-item-background-color:#E8F0FE;width:100%}.login-light .phone-field .react-international-phone-input-container{width:100%}.login-light .phone-field .react-international-phone-input{flex:1;min-width:0;font-family:inherit}.login-light .phone-field:focus-within .react-international-phone-input,.login-light .phone-field:focus-within .react-international-phone-country-selector-button{border-color:#1A73E8!important;box-shadow:0 0 0 1px #1A73E8}.login-light .react-international-phone-country-selector-button{background:#fff!important;border-color:#BDC1C6!important}.login-light .react-international-phone-country-selector-dropdown{background:#fff!important;color:#202124!important;z-index:60}.login-light .react-international-phone-country-selector-dropdown li{background:transparent!important}`}</style>
      <Banner />
      <SiteHeader />

      <main className="flex-1 bg-[linear-gradient(120deg,#EDF0FF_0%,#E8FFEF_55%,#FFF3EA_100%)]">
        <div className="mx-auto grid w-full max-w-[1280px] gap-10 px-4 py-12 lg:grid-cols-[0.95fr_1.05fr] lg:px-6 lg:py-16">
          {/* Left: message and perks, as on the register page */}
          <div className="flex flex-col">
            <span className="inline-flex self-start rounded-full bg-[#ffffff] px-4 py-1.5 text-[13px] font-bold uppercase tracking-[0.14em] text-[#0B57D0] shadow-sm">Welcome back</span>
            <h1 className="mt-6 text-[clamp(2.25rem,1.2rem+3.6vw,3.75rem)] font-medium leading-[1.08] tracking-[-0.025em] text-[#202124]">Sign in to your <span className="text-[#1A73E8]">group</span></h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-[#3C4043]">Collect contributions, run loans and meetings, and keep every member informed, from one dashboard.</p>
            <div className="mt-8 grid gap-4">
              {PERKS.map(({ Icon, title, text, tint, ink }) => (
                <div key={title} className="flex items-center gap-4 rounded-[24px] p-5" style={{ backgroundColor: tint }}>
                  <span className="grid size-12 shrink-0 place-items-center rounded-full bg-[#ffffff]" style={{ color: ink }}><Icon size={24} /></span>
                  <div><h2 className="font-semibold text-[#202124]">{title}</h2><p className="text-[15px] text-[#3C4043]">{text}</p></div>
                </div>
              ))}
            </div>
            <img src={asset("assets/auth/tint-team-spirit.svg")} alt="" aria-hidden className="mt-8 hidden w-72 self-start lg:block" />
          </div>

          {/* Right: the sign-in card */}
          <div className="self-start rounded-[32px] bg-[#ffffff] p-6 shadow-[0_8px_30px_rgba(26,115,232,.12)] sm:p-9">
            {step === 1 ? (
              <form onSubmit={handleContinue} noValidate>
                <h2 className="text-2xl font-semibold tracking-[-0.01em] text-[#202124]">Sign in</h2>
                <p className="mt-1 text-[15px] text-[#5F6368]">Enter your phone number and we'll send you a verification code.</p>
                {error && <div role="alert" className="mt-5 rounded-2xl bg-[#FCE8E6] px-4 py-3 text-[15px] text-[#C5221F]">{error}</div>}

                <div className="mt-6">
                  <PhoneField label="Phone number" value={phone} onChange={setPhone} autoFocus />
                </div>

                <button type="submit" disabled={isLoading} className={pill + " mt-6"}>
                  {isLoading ? <><Loader2 size={18} className="animate-spin" /> Sending code...</> : <>Continue <ArrowRight size={18} /></>}
                </button>
                <p className="mt-5 text-center text-[15px] text-[#5F6368]">New to Twachanga? <a href={site + "/register"} className={link}>Create a group</a></p>
                <p className="mt-3 flex items-center justify-center gap-2 text-xs text-[#5F6368]"><ShieldCheck size={14} /> Passwordless and secure. No password to remember.</p>
              </form>
            ) : (
              <form onSubmit={handleVerify} noValidate>
                <h2 className="text-2xl font-semibold tracking-[-0.01em] text-[#202124]">Enter your code</h2>
                <p className="mt-1 text-[15px] text-[#5F6368]">We sent a 6-digit code to <strong className="font-semibold text-[#202124]">{phoneOrEmail}</strong>{!phone.startsWith("+254") ? ", on WhatsApp" : ""}.</p>
                {error && <div role="alert" className="mt-5 rounded-2xl bg-[#FCE8E6] px-4 py-3 text-[15px] text-[#C5221F]">{error}</div>}

                <div className="mt-6">
                  <OTPInput
                    maxLength={6} value={code} onChange={setCode} pattern={REGEXP_ONLY_DIGITS} inputMode="numeric" autoComplete="one-time-code" autoFocus
                    onComplete={() => handleVerify()} containerClassName="group flex items-center has-[:disabled]:opacity-50"
                    render={({ slots }) => (
                      <div className="flex w-full justify-between gap-2">
                        {slots.map((slot, i) => (
                          <div key={i} className={"relative grid h-14 flex-1 place-items-center rounded-[14px] border bg-[#ffffff] text-2xl font-medium text-[#202124] transition " + (slot.isActive ? "border-[#1A73E8] ring-1 ring-[#1A73E8]" : "border-[#BDC1C6]")}>
                            {slot.char}
                            {slot.hasFakeCaret && <span className="absolute h-6 w-px animate-pulse bg-[#1A73E8]" />}
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </div>

                <button type="submit" disabled={isVerifying || code.length !== 6} className={pill + " mt-6"}>
                  {isVerifying ? <><Loader2 size={18} className="animate-spin" /> Signing in...</> : <><KeyRound size={18} /> Sign in</>}
                </button>
                <div className="mt-5 flex items-center justify-between text-[15px]">
                  <button type="button" onClick={back} className={link + " inline-flex items-center gap-1.5"}><ArrowLeft size={16} /> Change number</button>
                  {resendTimer > 0 && !isResending ? <span className="text-[#5F6368]">Resend in {resendTimer}s</span>
                    : <button type="button" onClick={handleResend} disabled={isResending} className={link + " disabled:opacity-60"}>{isResending ? "Sending..." : "Resend code"}</button>}
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default Login;
